import { normalizeS3Key, getFilenameFromPath } from "@/lib/utils";
import { ReadQuestionnaire } from "@/types/questionnaire";
import {
  ATTACHMENT_FIELD_NAMES,
  QuestionnaireAttachmentMeta,
} from "../hooks/attachment";

/**
 * Builds a map of attachment fields with their corresponding files.
 * Transforms raw attachment data from API into structured format with metadata.
 * Each attachment field (e.g., student_id_attachments, visa_attachments) is mapped
 * to an array of files with filename, s3Key, and size.
 */
export const buildAttachmentsMap = (
  rawAttachments: Record<string, unknown>,
): Record<string, QuestionnaireAttachmentMeta[]> => {
  const result: Record<string, QuestionnaireAttachmentMeta[]> = {};

  for (const field of ATTACHMENT_FIELD_NAMES) {
    const fieldData = rawAttachments[field];
    result[field] = Array.isArray(fieldData)
      ? fieldData.map<QuestionnaireAttachmentMeta>(
          (file: {
            id: string | null;
            path: string;
            size: string;
            confirmed_at?: string | null;
            can_delete: boolean;
          }) => ({
            id: file.id,
            filename: getFilenameFromPath(file.path),
            s3Key: normalizeS3Key(file.path),
            size: file.size,
            confirmed_at: file.confirmed_at,
            canDelete: file.can_delete,
          }),
        )
      : [];
  }

  return result;
};

/**
 * Builds form values for attachment fields.
 * Populates fields with attachments from API if form fields are empty.
 */
export const buildFormAttachmentValues = (
  attachments: Record<string, QuestionnaireAttachmentMeta[]>,
  questionnaireData: ReadQuestionnaire["questionnaire"],
): Record<string, unknown> => {
  const formValues: Record<string, unknown> = {};
  const questionnaireRecord = questionnaireData as Record<string, unknown>;

  for (const field of ATTACHMENT_FIELD_NAMES) {
    const files = attachments[field];
    const currentValue = questionnaireRecord[field];
    const hasExistingFormValue =
      Array.isArray(currentValue) && currentValue.length > 0;

    if (!hasExistingFormValue && files.length > 0) {
      formValues[field] = files.map(({ filename, s3Key }) => ({
        filename,
        s3_key: s3Key,
      }));
    }
  }

  return formValues;
};
